import React, { useState } from 'react';

function fmtUptime(sec: number) {
  if (!sec || sec < 0) return '—';
  if (sec < 60) return sec + 's';
  if (sec < 3600) return Math.floor(sec / 60) + 'm';
  const h = Math.floor(sec / 3600);
  if (h < 24) return h + 'h ' + Math.floor((sec % 3600) / 60) + 'm';
  return Math.floor(h / 24) + 'd ' + (h % 24) + 'h';
}

function fmtBytes(b: number) {
  if (b == null || b === 0) return '0 B';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  if (b < 1073741824) return (b / 1048576).toFixed(1) + ' MB';
  return (b / 1073741824).toFixed(2) + ' GB';
}

function fmtMbps(bytesPerSec: number) {
  if (!bytesPerSec) return '0 Mbps';
  const mbps = (bytesPerSec * 8) / 1000000;
  if (mbps < 0.01) return (bytesPerSec * 8 / 1000).toFixed(1) + ' Kbps';
  return mbps.toFixed(2) + ' Mbps';
}

function fmtMBps(b: number) {
  if (!b) return '0 B/s';
  if (b < 1024) return b.toFixed(0) + ' B/s';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB/s';
  return (b / 1048576).toFixed(1) + ' MB/s';
}

// Memory column cell. Splits what a service HOLDS from what the kernel merely
// charged to its cgroup — the two are wildly different for the video services.
// ingest-recording writes ~9 GB/h, so GBs of page cache land on its cgroup while
// it holds ~130 MB; postgres is the mirror image, holding shared_buffers as shmem
// that RSS alone does not see. `held` is anon + shmem (unreclaimable), `cache` is
// page cache + slab (reclaimable). A machine whose textfile collector predates
// node_cgroup_memory_* sends shmem = null; then the delta is labelled cache/shm
// because RSS and the working set cannot tell the two apart.
function MemoryCell({ m }: { m: any }) {
  const held = m?.memory_bytes || 0;
  const shmem = (m?.memory_shmem_bytes ?? null);
  const cache = m?.memory_cache_bytes ?? Math.max(0, (m?.memory_working_set_bytes || 0) - held);
  const showShm = shmem !== null && shmem > 64 * 1048576;
  const showCache = cache > 64 * 1048576;
  const title = shmem !== null
    ? `holds ${fmtBytes(held)} (anon ${fmtBytes(held - shmem)}${shmem > 0 ? ` + shared ${fmtBytes(shmem)}` : ''}) — unreclaimable\n`
      + `${fmtBytes(cache)} page cache + kernel slab charged to this cgroup — reclaimable on demand\n`
      + `${fmtBytes(held + cache)} total (what systemctl status reports)`
    : `${fmtBytes(held)} RSS — ${fmtBytes(cache)} of page cache / slab / shmem is also charged to this cgroup. `
      + `This machine's metrics collector cannot split reclaimable cache from held shared memory; redeploy it to get the breakdown.`;
  return (
    <span title={title}>
      <span className="block">{fmtBytes(held)}</span>
      {(showShm || showCache) && (
        <span className="block text-[9px] font-normal" style={{ color: 'var(--muted-foreground)' }}>
          {showShm && <>shared {fmtBytes(shmem)}{showCache ? ' · ' : ''}</>}
          {showCache && <>+{fmtBytes(cache)} {shmem !== null ? 'cache' : 'cache/shm'}</>}
        </span>
      )}
    </span>
  );
}

function ProgressBar({ percent, className = '' }: { percent: number; className?: string }) {
  const cls = percent > 90 ? 'bg-red-500' : percent > 75 ? 'bg-amber-500' : percent > 60 ? 'bg-yellow-500' : 'bg-emerald-500';
  return (
    <div className={`w-full h-1.5 rounded-full ${className}`} style={{ background: 'var(--muted)' }}>
      <div className={`h-full rounded-full transition-all duration-500 ${cls}`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

export default function PerformanceTab({ data }: { data: any }) {
  const [expandedSvc, setExpandedSvc] = useState<string | null>(null);
  const [selectedMachine, setSelectedMachine] = useState('all');

  const machines = data?.machines || {};
  const machineNames = Object.keys(machines);

  if (machineNames.length === 0) {
    return <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No machine data available</div>;
  }

  const filteredMachines = selectedMachine === 'all'
    ? machineNames
    : machineNames.filter(m => m === selectedMachine);

  return (
    <div className="space-y-4">
      {/* Machine selector */}
      {machineNames.length > 1 && (
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => setSelectedMachine('all')}
            className="px-3 py-1.5 text-xs rounded-md transition-colors"
            style={{
              background: selectedMachine === 'all' ? 'var(--brand)' : 'var(--input-background)',
              color: selectedMachine === 'all' ? '#fff' : 'var(--muted-foreground)',
              border: '1px solid var(--border)',
            }}
          >All Machines</button>
          {machineNames.map(mn => (
            <button
              key={mn}
              onClick={() => setSelectedMachine(mn)}
              className="px-3 py-1.5 text-xs rounded-md transition-colors"
              style={{
                background: selectedMachine === mn ? 'var(--brand)' : 'var(--input-background)',
                color: selectedMachine === mn ? '#fff' : 'var(--muted-foreground)',
                border: '1px solid var(--border)',
              }}
            >🖥 {mn}</button>
          ))}
        </div>
      )}

      {/* Machine cards */}
      {filteredMachines.map(mn => {
        const machine = machines[mn];
        const h = machine.host || {};
        const serviceEntries = Object.entries(machine.services || {});

        return (
          <div key={mn} className="rounded-lg overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            {/* Machine header */}
            <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border)', background: 'var(--input-background)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${h.online ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>🖥 {mn}</span>
                  <span className="text-[10px] font-mono" style={{ color: 'var(--muted-foreground)' }}>
                    ({serviceEntries.length} services)
                  </span>
                </div>
                <span className="text-[10px] font-mono" style={{ color: 'var(--muted-foreground)' }}>
                  Uptime: {fmtUptime(h.uptime_seconds)}
                </span>
              </div>

              {/* Host metrics row */}
              {h.online && (
                <div className="mt-3 grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2.5 text-[11px]">
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1 truncate">
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>CPU</span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--foreground)' }}>{h.cpu_usage_percent?.toFixed(1)}%</span>
                      <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>({h.cpu_cores}c)</span>
                    </div>
                    <ProgressBar percent={h.cpu_usage_percent} className="mt-1" />
                    {h.cpu_model && (
                      <div className="text-[9px] truncate mt-0.5" style={{ color: 'var(--muted-foreground)' }} title={h.cpu_model}>{h.cpu_model}</div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1 truncate">
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>MEM</span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--foreground)' }}>{h.memory_usage_percent?.toFixed(1)}%</span>
                      <span className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>({fmtBytes(h.memory_used_bytes)}/{fmtBytes(h.memory_total_bytes)})</span>
                    </div>
                    <ProgressBar percent={h.memory_usage_percent} className="mt-1" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1 truncate">
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>DISK</span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--foreground)' }}>{h.disk_usage_percent?.toFixed(1)}%</span>
                      <span className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>({fmtBytes(h.disk_total_bytes - h.disk_used_bytes)} free / {fmtBytes(h.disk_total_bytes)})</span>
                    </div>
                    <ProgressBar percent={h.disk_usage_percent} className="mt-1" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1">
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>LOAD</span>
                      <span className="font-mono" style={{ color: 'var(--foreground)' }}>{h.load_avg_1m?.toFixed(1)} / {h.load_avg_5m?.toFixed(1)} / {h.load_avg_15m?.toFixed(1)}</span>
                    </div>
                    <div className="text-[9px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>1m / 5m / 15m</div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1">
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>TEMP</span>
                      <span className="font-mono" style={{ color: 'var(--foreground)' }}>
                        {h.cpu_temp_celsius > 0 ? h.cpu_temp_celsius.toFixed(0) + '°C' : '—'}
                      </span>
                      {h.gpu_temp_celsius > 0 && (
                        <span className="font-mono text-[9px]" style={{ color: 'var(--muted-foreground)' }}>(GPU {h.gpu_temp_celsius.toFixed(0)}°C)</span>
                      )}
                    </div>
                  </div>
                  {(h.gpu_utilization_percent > 0 || h.gpu_temp_celsius > 0 || h.gpu_memory_total_mb > 0) && (
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-1 truncate">
                        <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>GPU</span>
                        <span className="font-mono font-semibold" style={{ color: 'var(--foreground)' }}>{h.gpu_utilization_percent?.toFixed(0)}%</span>
                      </div>
                      <ProgressBar percent={h.gpu_utilization_percent} className="mt-1" />
                      {h.gpu_name && (
                        <div className="text-[9px] truncate mt-0.5" style={{ color: 'var(--muted-foreground)' }} title={h.gpu_name}>{h.gpu_name}</div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>
                        <span>ENC {(h.gpu_encoder_percent || 0).toFixed(0)}%</span>
                        <span>DEC {(h.gpu_decoder_percent || 0).toFixed(0)}%</span>
                        {h.gpu_memory_total_mb > 0 && (
                          <span>
                            VRAM {(h.gpu_memory_usage_percent || 0).toFixed(0)}%
                            {' '}({fmtBytes(h.gpu_memory_used_mb * 1024 * 1024)}/{fmtBytes(h.gpu_memory_total_mb * 1024 * 1024)})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Network interfaces */}
              {machine.network_interfaces && machine.network_interfaces.length > 0 && (
                <div className="mt-2.5 flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-[11px] font-mono">
                  {machine.network_interfaces.filter((i: any) => i.rx_bytes_per_sec > 0 || i.tx_bytes_per_sec > 0).map((iface: any) => (
                    <span key={iface.device} className="inline-flex items-center gap-1.5">
                      <span className="text-[10px] font-semibold" style={{ color: 'var(--muted-foreground)' }}>{iface.device}</span>
                      <span className="text-emerald-400">↓{fmtMbps(iface.rx_bytes_per_sec)}</span>
                      <span className="text-blue-400">↑{fmtMbps(iface.tx_bytes_per_sec)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Services table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: 'var(--input-background)' }}>
                    <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>Service</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>CPU</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }} title="Memory the service holds (anonymous + shared). Page cache and kernel slab charged to its cgroup are reclaimable and shown separately.">Memory</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>IO Read</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>IO Write</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>Net RX</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>Net TX</th>
                    <th className="text-right px-2 py-2 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>Containers</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceEntries.map(([sg, svc]: [string, any]) => {
                    const isExpanded = expandedSvc === `${mn}/${sg}`;
                    return (
                      <React.Fragment key={sg}>
                        <tr
                          className="border-t cursor-pointer hover:bg-white/5 transition-colors"
                          style={{ borderColor: 'var(--border)' }}
                          onClick={() => setExpandedSvc(isExpanded ? null : `${mn}/${sg}`)}
                        >
                          <td className="px-3 py-2 font-medium" style={{ color: 'var(--foreground)' }}>
                            <span className="text-[10px] mr-1">{isExpanded ? '▾' : '▸'}</span>
                            {sg}
                          </td>
                          <td className="text-right px-2 py-2 font-mono">{(svc.cpu_percent || 0).toFixed(1)}%</td>
                          <td className="text-right px-2 py-2 font-mono"><MemoryCell m={svc} /></td>
                          <td className="text-right px-2 py-2 font-mono">{fmtMBps(svc.disk_read_bytes_per_sec)}</td>
                          <td className="text-right px-2 py-2 font-mono">{fmtMBps(svc.disk_write_bytes_per_sec)}</td>
                          <td className="text-right px-2 py-2 font-mono text-emerald-400">{fmtMbps(svc.net_rx_bytes_per_sec)}</td>
                          <td className="text-right px-2 py-2 font-mono text-blue-400">{fmtMbps(svc.net_tx_bytes_per_sec)}</td>
                          <td className="text-right px-2 py-2 font-mono" style={{ color: 'var(--muted-foreground)' }}>{svc.containers?.length || 0}</td>
                        </tr>
                        {isExpanded && svc.containers && svc.containers.length > 0 && (
                          <tr style={{ background: 'var(--input-background)' }}>
                            <td colSpan={8} className="px-6 py-2">
                              <table className="w-full text-[10px]">
                                <thead>
                                  <tr style={{ color: 'var(--muted-foreground)' }}>
                                    <th className="text-left px-2 py-1">Container</th>
                                    <th className="text-right px-2 py-1">CPU %</th>
                                    <th className="text-right px-2 py-1" title="Memory held (anonymous + shared); reclaimable cache shown separately">Memory</th>
                                    <th className="text-right px-2 py-1">IO Read</th>
                                    <th className="text-right px-2 py-1">IO Write</th>
                                    <th className="text-right px-2 py-1">Net RX</th>
                                    <th className="text-right px-2 py-1">Net TX</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {svc.containers.map((c: any) => (
                                    <tr key={c.name} className="border-t" style={{ borderColor: 'var(--border)' }}>
                                      <td className="px-2 py-1 font-mono" style={{ color: 'var(--foreground)' }}>{c.name}</td>
                                      <td className="text-right px-2 py-1 font-mono">{c.cpu_percent.toFixed(2)}%</td>
                                      <td className="text-right px-2 py-1 font-mono"><MemoryCell m={c} /></td>
                                      <td className="text-right px-2 py-1 font-mono">{fmtMBps(c.disk_read_bytes_per_sec)}</td>
                                      <td className="text-right px-2 py-1 font-mono">{fmtMBps(c.disk_write_bytes_per_sec)}</td>
                                      <td className="text-right px-2 py-1 font-mono text-emerald-400">{fmtMbps(c.net_rx_bytes_per_sec)}</td>
                                      <td className="text-right px-2 py-1 font-mono text-blue-400">{fmtMbps(c.net_tx_bytes_per_sec)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
